import { useCallback, useMemo, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  type Node as RFNode,
  type Edge as RFEdge,
  type Connection,
  type NodeChange,
  type EdgeChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import type { Workflow, WorkflowNode, WorkflowNodeType } from "@/types/aiToolkit";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useWorkflowRun } from "@/hooks/useWorkflowRun";
import { CanvasNode } from "./CanvasNode";
import { NodeConfigPanel } from "./NodeConfigPanel";
import { NODE_META, PALETTE_ORDER } from "./nodeMeta";
import { toReactFlow, toWorkflowGraph } from "./graphConvert";
import { useWorkflowRunStatus } from "./useWorkflowRunStatus";
import { STATUS_COLORS } from "./runStatusPure";

/**
 * Phase 4A.1/4A.2 — React Flow canvas over the EXISTING workflow engine.
 * Additive/experimental: it round-trips the same ai_workflows JSON the engine
 * runs (graphConvert), saves via onSave (same contract as WorkflowBuilder), and
 * runs via the existing useWorkflowRun. It does NOT replace WorkflowBuilder.tsx.
 */
export function WorkflowCanvas({
  workflow,
  onSave,
}: {
  workflow?: Workflow;
  onSave?: (patch: Partial<Workflow>) => void | Promise<void>;
}) {
  const { toast } = useToast();
  const { startWorkflow, isRunning, progressLabel, activeRunId } = useWorkflowRun();
  // 4A.8: live per-node status polled from workflow_run_events while active.
  const { runStatus, nodeStatuses, active } = useWorkflowRunStatus(activeRunId);

  // Authoritative engine nodes (full data). RF state is derived + positions.
  const [engineNodes, setEngineNodes] = useState<WorkflowNode[]>(workflow?.nodes ?? []);
  const initial = useMemo(
    () => toReactFlow({ nodes: workflow?.nodes ?? [], edges: workflow?.edges ?? [] }),
    [workflow],
  );
  const [rfNodes, setRfNodes] = useState<RFNode[]>(initial.nodes);
  const [rfEdges, setRfEdges] = useState<RFEdge[]>(initial.edges);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  const nodeTypes = useMemo(() => ({ canvas: CanvasNode }), []);

  const onNodesChange = useCallback((c: NodeChange[]) => setRfNodes((ns) => applyNodeChanges(c, ns)), []);
  const onEdgesChange = useCallback((c: EdgeChange[]) => setRfEdges((es) => applyEdgeChanges(c, es)), []);
  const onConnect = useCallback(
    (conn: Connection) =>
      setRfEdges((es) => addEdge({ ...conn, id: `e-${conn.source}-${conn.target}-${es.length}` }, es)),
    [],
  );

  const addNode = useCallback((type: WorkflowNodeType) => {
    const id = `node-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
    const engineNode: WorkflowNode = {
      id, type,
      position: { x: 120 + Math.random() * 240, y: 80 + Math.random() * 180 },
      data: { label: NODE_META[type].label },
    };
    setEngineNodes((ns) => [...ns, engineNode]);
    setRfNodes((ns) => [
      ...ns,
      { id, type: "canvas", position: engineNode.position, data: { nodeType: type, label: engineNode.data.label } },
    ]);
  }, []);

  const patchNodeData = useCallback((nodeId: string, patch: Partial<WorkflowNode["data"]>) => {
    setEngineNodes((ns) => ns.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n)));
    if (patch.label !== undefined) {
      setRfNodes((ns) => ns.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, label: patch.label } } : n)));
    }
  }, []);

  const deleteNode = useCallback((nodeId: string) => {
    setEngineNodes((ns) => ns.filter((n) => n.id !== nodeId));
    setRfNodes((ns) => ns.filter((n) => n.id !== nodeId));
    setRfEdges((es) => es.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setSelectedId((s) => (s === nodeId ? null : s));
  }, []);

  const buildGraph = useCallback((): Pick<Workflow, "nodes" | "edges" | "entryNodeId"> => {
    const graph = toWorkflowGraph(rfNodes, rfEdges, engineNodes);
    const entry = graph.nodes.find((n) => n.type === "trigger")?.id ?? graph.nodes[0]?.id ?? "";
    return { nodes: graph.nodes, edges: graph.edges, entryNodeId: entry };
  }, [rfNodes, rfEdges, engineNodes]);

  const handleSave = useCallback(async () => {
    const graph = buildGraph();
    if (!onSave) {
      toast({ title: "Preview mode", description: "No save target wired — graph is valid and runnable." });
      return;
    }
    await onSave(graph);
    toast({ title: "Workflow saved" });
  }, [buildGraph, onSave, toast]);

  const handleRun = useCallback(async () => {
    if (!workflow) {
      toast({ title: "Save first", description: "Runs execute a saved workflow record." });
      return;
    }
    const graph = buildGraph();
    const run = await startWorkflow({ ...workflow, ...graph } as Workflow, {}, { triggerType: "manual" });
    toast({
      title: run ? "Run started" : "Run failed to start",
      description: run ? `Run ${run.id.slice(0, 8)}…` : undefined,
      variant: run ? undefined : "destructive",
    });
  }, [workflow, buildGraph, startWorkflow, toast]);

  const setEdgeMapping = useCallback((edgeId: string, mapping: Record<string, string>) => {
    setRfEdges((es) =>
      es.map((e) =>
        e.id === edgeId
          ? {
              ...e,
              data: { ...(e.data ?? {}), dataMapping: mapping },
              label: Object.keys(mapping).length ? `⤷ ${Object.keys(mapping).join(", ")}` : undefined,
            }
          : e,
      ),
    );
  }, []);

  const selectedEngineNode = engineNodes.find((n) => n.id === selectedId) ?? null;
  const selectedEdge = rfEdges.find((e) => e.id === selectedEdgeId) ?? null;

  // 4A.8: decorate RF nodes with live run status (derived, never persisted).
  const decoratedNodes = useMemo(() => {
    if (!Object.keys(nodeStatuses).length) return rfNodes;
    return rfNodes.map((n) => {
      const info = nodeStatuses[n.id];
      if (!info) return n;
      return {
        ...n,
        data: { ...n.data, runStatus: info.status, runMs: info.durationMs, runCompletions: info.completions },
      };
    });
  }, [rfNodes, nodeStatuses]);

  const selectedRunOutput = selectedId ? nodeStatuses[selectedId]?.outputSnapshot : undefined;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", minHeight: 480 }}>
      {/* Toolbar */}
      <div style={{ position: "absolute", top: 10, left: 10, zIndex: 4, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", maxWidth: "calc(100% - 340px)" }}>
        <NodePalette onAdd={addNode} />
        <Button size="sm" onClick={handleSave}>Save</Button>
        <Button size="sm" variant="secondary" onClick={handleRun} disabled={isRunning}>
          {isRunning ? (progressLabel ?? "Running…") : "Run"}
        </Button>
      </div>

      {/* 4A.8: run status bar */}
      {activeRunId && runStatus && (
        <div style={{ position: "absolute", bottom: 10, left: 10, zIndex: 4, display: "flex", gap: 8, alignItems: "center", background: "var(--card, #fff)", color: "var(--card-foreground, #0f172a)", border: "1px solid var(--border, #e2e8f0)", borderRadius: 8, padding: "6px 10px", fontSize: 12 }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: runStatus === "completed" ? STATUS_COLORS.completed : runStatus === "failed" ? STATUS_COLORS.failed : runStatus === "waiting_approval" ? STATUS_COLORS.waiting : STATUS_COLORS.running, animation: active ? "pulse 1.2s infinite" : undefined }} />
          run <code>{activeRunId.slice(0, 8)}</code> · {runStatus}
          {active ? " · live" : ""}
        </div>
      )}

      <ReactFlow
        nodes={decoratedNodes}
        edges={rfEdges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={(_, n) => { setSelectedId(n.id); setSelectedEdgeId(null); }}
        onEdgeClick={(_, e) => { setSelectedEdgeId(e.id); setSelectedId(null); }}
        onPaneClick={() => { setSelectedId(null); setSelectedEdgeId(null); }}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls />
        <MiniMap pannable zoomable />
      </ReactFlow>

      <style>{`@keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }`}</style>

      {selectedEngineNode && (
        <NodeConfigPanel
          node={selectedEngineNode}
          onChange={patchNodeData}
          onDelete={deleteNode}
          onClose={() => setSelectedId(null)}
          runOutput={selectedRunOutput}
        />
      )}

      {selectedEdge && (
        <EdgeMappingPanel
          edge={selectedEdge}
          onChange={setEdgeMapping}
          onClose={() => setSelectedEdgeId(null)}
        />
      )}
    </div>
  );
}

/** 4A.5 — edit an edge's dataMapping (target-input-key = upstream path per line). */
function EdgeMappingPanel({
  edge,
  onChange,
  onClose,
}: {
  edge: RFEdge;
  onChange: (edgeId: string, mapping: Record<string, string>) => void;
  onClose: () => void;
}) {
  const current = (edge.data as { dataMapping?: Record<string, string> } | undefined)?.dataMapping ?? {};
  const text = Object.entries(current).map(([k, v]) => `${k} = ${v}`).join("\n");
  return (
    <div style={{ position: "absolute", top: 0, right: 0, height: "100%", width: 300, background: "var(--card, #fff)", color: "var(--card-foreground, #0f172a)", borderLeft: "1px solid var(--border, #e2e8f0)", padding: 14, overflowY: "auto", zIndex: 5 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <strong>Edge data mapping</strong>
        <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
      </div>
      <p style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
        One <code>targetKey = upstream.path</code> per line. The target node receives these as inputs
        (agent: MAPPED INPUTS / <code>content</code> overrides the message; http: <code>{"{{inputs.key}}"}</code>).
      </p>
      <textarea
        defaultValue={text}
        onBlur={(e) => {
          const mapping: Record<string, string> = {};
          for (const line of e.target.value.split("\n")) {
            const [k, ...rest] = line.split("=");
            const key = k.trim();
            const path = rest.join("=").trim();
            if (key && path) mapping[key] = path;
          }
          onChange(edge.id, mapping);
        }}
        placeholder={"content = nodeOutputs.node-1.raw\ntitle = nodeOutputs.node-1.parsed.title"}
        style={{ width: "100%", minHeight: 140, fontFamily: "monospace", fontSize: 12, padding: 8, borderRadius: 6, border: "1px solid var(--border, #e2e8f0)", background: "transparent", color: "inherit" }}
      />
      <p style={{ fontSize: 11, opacity: 0.55, marginTop: 6 }}>Paths: <code>variables.x</code>, <code>nodeOutputs.&lt;nodeId&gt;.raw</code>, <code>nodeOutputs.&lt;nodeId&gt;.parsed.field</code>. Applied on blur; Save to persist.</p>
    </div>
  );
}

function NodePalette({ onAdd }: { onAdd: (t: WorkflowNodeType) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)}>+ Add node</Button>
      {open && (
        <div
          style={{
            position: "absolute", top: 38, left: 0, zIndex: 6, width: 220, maxHeight: 320, overflowY: "auto",
            background: "var(--popover, #fff)", color: "var(--popover-foreground, #0f172a)",
            border: "1px solid var(--border, #e2e8f0)", borderRadius: 8, padding: 6, boxShadow: "0 6px 24px rgba(0,0,0,0.18)",
          }}
        >
          {PALETTE_ORDER.map((t) => {
            const m = NODE_META[t];
            return (
              <button
                key={t}
                onClick={() => { onAdd(t); setOpen(false); }}
                style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "6px 8px", border: "none", background: "transparent", color: "inherit", cursor: "pointer", borderRadius: 6, fontSize: 13, textAlign: "left" }}
              >
                <span style={{ width: 8, height: 8, borderRadius: 999, background: m.color }} />
                {m.label}
                {!m.executes && <span style={{ marginLeft: "auto", fontSize: 10, color: "#b45309" }}>stub</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
